import { IndicationSet } from 'features/indicationTriage'
import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { entryRouteToPartLevel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import {
  FfsPart,
  FfsResultType,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { ClassifiedLta } from 'features/libra/stepComponents/ffs/classifiedLtas'
import { selectGoverningReport } from 'features/libra/stepComponents/ffs/ffsGoverningReport'
import { FfsReportRecord } from 'features/libra/stepComponents/ffs/ffsReportRecord'
import { ScenarioKey } from 'features/libra/stepComponents/ffs/ffsScenarioKey'
import { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import { GeckoComponent } from 'types'

/**
 * Scenario-agnostic completion: an LTA is "assessment recorded" once ANY scenario
 * it was assessed in resolves to a terminal governing verdict (or Skipped) — via
 * the exact same best-of selector close-out uses. A Fail resolves it (terminal,
 * no forced escalation); a parse failure blocks it.
 *
 * The close-out step remains the authoritative PER-scenario gate; a part-step
 * only asks "has the engineer produced a usable assessment for this LTA at all?".
 * So a part-step CAN read "recorded" while close-out still blocks — e.g. the
 * LTA's only reports live in a scenario other than the unit's signed-off one.
 * That divergence is intended: the two gates answer different questions.
 */
export function hasRecordedAssessment(
  classification: FfsRegionClassification,
  reports: ReadonlyArray<FfsReportRecord>,
  parseFailures: number
): boolean {
  const scenarios = Array.from(
    new Set(
      reports
        .map((r) => r.scenarioKey)
        .filter((k): k is ScenarioKey => k !== null)
    )
  )
  return scenarios.some(
    (scenarioKey) =>
      selectGoverningReport({
        classification,
        reports,
        scenarioKey,
        parseFailures,
      }).kind === 'selected'
  )
}

export interface ComputeFfsPartValidationArgs {
  part: FfsPart
  componentId: string | undefined
  inspectionId: string | undefined
  component: GeckoComponent | undefined
  ltaSet: IndicationSet | undefined
  /**
   * LTA indications paired with their ALREADY-PARSED region classifications (see
   * `classifiedLtas`). Parsing happens once per data load at the step, not here.
   */
  classifiedLtas: ReadonlyArray<ClassifiedLta>
  /** ALL-part saved report records, keyed by indication id. */
  reportsByIndication: ReadonlyMap<string, ReadonlyArray<FfsReportRecord>>
  /** Per-indication count of reports that failed to parse (blocks that LTA). */
  parseFailuresByIndication: ReadonlyMap<string, number>
  isLoading: boolean
}

export interface ComputeFfsPartValidationResult {
  validation: StepValidationResult
  /**
   * DISPLAY denominator: in-scope classified LTAs PLUS unclassified LTAs. In the
   * un-planned state the header intentionally shows all LTAs, so an unclassified
   * LTA (route unknown) is counted in both parts' denominators. This is a display
   * figure only -- the blocking `validation` is computed over classified,
   * in-scope LTAs alone (see below), so unclassified LTAs never block completion.
   */
  eligibleLtaCount: number
  /** DISPLAY numerator: assessed LTAs among the display-eligible set. */
  assessedLtaCount: number
  /** Unclassified LTAs (missing or invalid classification); drives a UI warning. */
  unclassifiedLtaCount: number
}

const PART_LABEL: Record<FfsPart, string> = {
  [FfsPart.PART_4]: 'Part 4',
  [FfsPart.PART_5]: 'Part 5',
}

/**
 * Pure per-LTA completion validation for a part-scoped FFS step. Completion is
 * HOLISTIC (scenario-agnostic best-of): every classified, non-Type-C LTA is in
 * scope for BOTH part-steps, and it resolves once it has a recorded assessment
 * via ANY eligible cell (`hasRecordedAssessment`). A Type-C LTA is excluded (n/a).
 * Unclassified LTAs (missing or invalid classification) are permissive: they count
 * toward the DISPLAY denominator but never block completion, because FFS
 * Pre-Planning is skippable.
 */
export function computeFfsPartValidation({
  part,
  componentId,
  inspectionId,
  component,
  ltaSet,
  classifiedLtas,
  reportsByIndication,
  parseFailuresByIndication,
  isLoading,
}: ComputeFfsPartValidationArgs): ComputeFfsPartValidationResult {
  const label = PART_LABEL[part]
  // Prerequisite/blocked outcomes all report zero counts; this keeps the ladder legible.
  const fail = (
    validation: StepValidationResult
  ): ComputeFfsPartValidationResult => ({
    validation,
    eligibleLtaCount: 0,
    assessedLtaCount: 0,
    unclassifiedLtaCount: 0,
  })

  // Prerequisite tier ladder: tags -> loading -> component -> set -> indications.
  if (!inspectionId || !componentId) {
    return fail({
      status: 'Error',
      message: 'Ticket is missing required component or inspection tag.',
    })
  }
  if (isLoading) {
    return fail({ status: 'Warning', message: 'Loading indication data...' })
  }
  if (!component) {
    return fail({
      status: 'Error',
      message: 'Component entry not found in Rolodex.',
    })
  }
  if (!ltaSet) {
    return fail({
      status: 'Warning',
      message:
        'No LTA indication set found for this (component, inspection) yet. Generate one upstream before assessment.',
    })
  }
  const totalCount = classifiedLtas.length
  if (totalCount === 0) {
    return fail({
      status: 'Warning',
      message:
        'Indication set exists but has no indications -- re-run LTA generation if expected.',
    })
  }

  // Count in a single pass. A malformed/absent record parsed to `null` upstream
  // is an unclassified LTA; FFS Pre-Planning is skippable, so those are treated
  // PERMISSIVELY (a Level-1 entry, matching `computeFfsEligibility`): they count
  // toward the display totals but never block completion. A classified LTA is
  // IN SCOPE only when its entry route lives in this part.
  let inScopeLtaCount = 0
  let inScopeAssessedLtaCount = 0
  let unclassifiedLtaCount = 0
  let unclassifiedAssessedLtaCount = 0
  for (const { indication, classification } of classifiedLtas) {
    const reports = reportsByIndication.get(indication.id) ?? []
    const parseFailures = parseFailuresByIndication.get(indication.id) ?? 0
    if (classification === null) {
      unclassifiedLtaCount += 1
      // Permissive display only: any committed (non-Pending) report counts.
      if (reports.some((r) => r.result !== FfsResultType.Pending)) {
        unclassifiedAssessedLtaCount += 1
      }
      continue
    }
    // Type C (NEITHER_APPLICABLE) is not assessable anywhere — excluded from the
    // blocking scope entirely (n/a). Every other classified LTA is in scope for
    // BOTH part-steps: completion is holistic (best-of across all eligible cells),
    // so an LTA assessed via any eligible method resolves both steps.
    if (entryRouteToPartLevel(classification.entryRoute) === null) continue
    inScopeLtaCount += 1
    if (hasRecordedAssessment(classification, reports, parseFailures)) {
      inScopeAssessedLtaCount += 1
    }
  }

  // DISPLAY totals include unclassified LTAs (route unknown, so they show under
  // both parts) -- informational only. BLOCKING uses the in-scope-classified
  // counts alone, so missing classification never blocks the step.
  const eligibleLtaCount = inScopeLtaCount + unclassifiedLtaCount
  const assessedLtaCount =
    inScopeAssessedLtaCount + unclassifiedAssessedLtaCount

  let validation: StepValidationResult = undefined
  if (inScopeAssessedLtaCount < inScopeLtaCount) {
    const remaining = inScopeLtaCount - inScopeAssessedLtaCount
    validation = {
      status: 'Error',
      message: `${remaining} of ${inScopeLtaCount} LTA${inScopeLtaCount === 1 ? '' : 's'} still need a ${label} assessment.`,
    }
  }

  return {
    validation,
    eligibleLtaCount,
    assessedLtaCount,
    unclassifiedLtaCount,
  }
}
